import { selectors as authSelectors } from '@hixme/auth-ui'
import {
  ROLE_CLIENT_ADMIN,
  ROLE_PLATFORM_HIXME_ADMIN,
} from '@hixme/role-policy'
import { notification } from '@surecompanies/core_components'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMyClientsQuery } from 'enrollment-platform/api/persons'
import useAssigneeList from 'hooks/useAssigneeList'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  assignAncillaryDiscrepancy,
  downloadAncillaryReport,
  fetchAncillaryReport,
  fetchOpenableAncillaryReportClients,
  triggerAncillarySnapshot,
} from '../api/ancillary-report.api'
export const ANCILLARY_REPORT_QUERY_KEY = 'ancillary-report'
export const ANCILLARY_REPORT_CLIENTS_QUERY_KEY = 'ancillary-report-clients'

function responseStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined
  const response = (error as { response?: { status?: unknown } }).response
  return typeof response?.status === 'number' ? response.status : undefined
}

/**
 * A user-facing message for a failed report request.
 */
export function describeReportError(error: unknown): string {
  const status = responseStatus(error)
  if (status === 404) return 'No Guardian snapshot exists for this client yet.'
  if (status === 403) {
    return 'You do not have access to the ancillary dashboard for this client.'
  }
  return 'The ancillary dashboard could not be loaded.'
}

/**
 * A user-facing message for a failed snapshot trigger.
 */
export function describeSnapshotTriggerError(error: unknown): string {
  if (responseStatus(error) === 409) {
    return 'A snapshot run is already queued or in progress.'
  }
  return 'The snapshot could not be started.'
}

export function useAncillaryReport() {
  const queryClient = useQueryClient()
  const role = useSelector(authSelectors.getRole) as string | undefined
  const isPlatformAdmin = role === ROLE_PLATFORM_HIXME_ADMIN
  const assigneeOptions = useAssigneeList({ isIncludeSpecialAssignee: false })
  const canUseReport = isPlatformAdmin || role === ROLE_CLIENT_ADMIN
  const { data: myClients = [], isLoading: isMyClientsLoading } =
    useMyClientsQuery()

  const myClientIds = useMemo(
    () => myClients.map((client) => client.id),
    [myClients]
  )
  // Platform admins are offered every snapshot-enabled client; client admins
  // only their own clients that EPS enables for them.
  const openableQuery = useQuery({
    queryKey: [
      ANCILLARY_REPORT_CLIENTS_QUERY_KEY,
      isPlatformAdmin ? 'snapshot-enabled' : myClientIds,
    ],
    queryFn: () =>
      fetchOpenableAncillaryReportClients(
        isPlatformAdmin ? undefined : myClientIds
      ),
    enabled: canUseReport && myClientIds.length > 0,
  })

  const clients = useMemo(() => {
    const openableIds = new Set(openableQuery.data ?? [])
    return myClients.filter((client) => openableIds.has(client.id))
  }, [myClients, openableQuery.data])
  const isClientsLoading = isMyClientsLoading || openableQuery.isLoading

  const [selectedClientId, setSelectedClientId] = useState<string>()
  const clientPublicKey =
    clients.find((client) => client.id === selectedClientId)?.id ??
    clients[0]?.id

  const reportQuery = useQuery({
    queryKey: [ANCILLARY_REPORT_QUERY_KEY, clientPublicKey],
    queryFn: () => fetchAncillaryReport(clientPublicKey ?? ''),
    enabled: Boolean(clientPublicKey),
    retry: false,
  })

  const { mutate: download, isPending: isDownloading } = useMutation({
    mutationFn: () => downloadAncillaryReport(clientPublicKey ?? ''),
    onError: (error: unknown) => {
      notification.error({
        message: 'Download failed',
        description: describeReportError(error),
      })
    },
  })

  const { mutate: assign, isPending: isAssigning } = useMutation({
    mutationFn: (input: { benefitIds: string[]; assignee: string | null }) =>
      assignAncillaryDiscrepancy({
        clientPublicKey: clientPublicKey ?? '',
        benefitIds: input.benefitIds,
        assignee: input.assignee,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ANCILLARY_REPORT_QUERY_KEY, clientPublicKey],
      })
    },
    onError: () => {
      notification.error({
        message: 'Assignment failed',
        description: 'The assignment could not be saved.',
      })
    },
  })

  const { mutate: triggerSnapshot, isPending: isTriggering } = useMutation({
    mutationFn: () => triggerAncillarySnapshot(),
    onSuccess: () => {
      notification.success({
        message: 'Snapshot queued',
        description: 'The snapshot was queued.',
      })
    },
    onError: (error: unknown) => {
      notification.error({
        message: 'Snapshot failed',
        description: describeSnapshotTriggerError(error),
      })
    },
  })

  return {
    clients,
    isClientsLoading,
    clientPublicKey,
    selectClient: setSelectedClientId,
    report: reportQuery.data,
    isReportLoading: reportQuery.isLoading,
    reportError: reportQuery.error,
    download,
    isDownloading,
    isPlatformAdmin,
    assigneeOptions,
    assign,
    isAssigning,
    triggerSnapshot,
    isTriggering,
  }
}
